<template>
  <v-dialog v-model="dialog" persistent max-width="800px" data-cy="quickFilterManager">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        class="ml-2"
        color="primary"
        dark
        data-cy="configureQuickFiltersButton"
        outlined
        small
        v-bind="attrs"
        v-on="on"
      >
        <v-icon class="mr-2" small>fas fa-cog</v-icon>Configure
      </v-btn>
    </template>
    <v-card data-cy="quickFilterManagerDialog">
      <v-card-title>
        <span class="text-h5 font-weight-thin">Manage Quick Filters</span>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-row class="mt-5">
          <v-col cols="8">
            <v-card outlined>
              <v-card-title class="px-4 py-1">
                <span class="overline"><v-icon class="ml-1 mr-1" x-small>fas fa-bolt</v-icon> Quick Filters</span>
              </v-card-title>
              <v-card-text data-cy="quickFilterManagerList">
                <draggable
                  @end="drag = false"
                  @start="drag = true"
                  data-cy="quickFilterCardList"
                  group="people"
                  v-model="quickFilterList"
                >
                  <div class="pointer" v-for="(quickFilter, index) in quickFilterList" :key="quickFilter.presetName">
                    <v-card
                      outlined
                      class="mb-1"
                      color="grey lighten-4"
                      :data-cy="`quickFilterCard-${getDataCySuffix(quickFilter.presetName)}`"
                    >
                      <v-card-text class="py-2">
                        <v-row>
                          <v-col md="9"
                            ><v-icon class="mr-2" x-small color="grey lighten-1">fas fa-grip-horizontal</v-icon>
                            <span class="quickFilterPresetName">{{ quickFilter.presetName }}</span></v-col
                          >
                          <v-col md="3" class="text-right"
                            ><v-btn
                              :data-cy="`deleteQuickFilter-${getDataCySuffix(quickFilter.presetName)}`"
                              icon
                              small
                              @click="removeQuickFilter(index)"
                              ><v-icon x-small>fas fa-trash-alt</v-icon></v-btn
                            ></v-col
                          >
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </div>
                </draggable>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="4">
            <v-autocomplete
              :item-text="item => `${item.presetName} (${item.presetType})`"
              :items="aggregatedPresetList"
              @change="addQuickFilter"
              data-cy="availableQuickFilterInput"
              dense
              hide-details
              outlined
              placeholder="Add Quick Filter..."
              return-object
              v-model="selectedQuickFilter"
            ></v-autocomplete>

            <div class="ml-1 mr-1 mt-6">
              <div><b>Custom Quick Filters:</b> Filters that have been previously saved within your organization.</div>
              <div class="mt-3">
                <b>System Quick Filters:</b> Filters that have been curated by OCHIN as good starter quick filters.
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider class="mt-3"></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="dialog = false">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { differenceWith, isEqual } from "lodash";

import draggable from "vuedraggable";

export default {
  components: {
    draggable,
  },
  computed: {
    aggregatedPresetList() {
      const sortedAggregatedList = [...this.customPresetList, ...this.systemPresetList].sort((a, b) =>
        a.presetName < b.presetName ? -1 : 1
      );
      return differenceWith(sortedAggregatedList, this.quickFilterList, isEqual);
    },
    customPresetList() {
      return this.presetList.map(preset => {
        return {
          presetName: preset.name,
          presetType: "custom",
        };
      });
    },
    quickFilterList: {
      get() {
        return this.$store.state.quickFilter.list;
      },
      async set(value) {
        await this.$store.dispatch("quickFilter/update", value);
      },
    },
    systemPresetList() {
      return this.systemList.map(preset => {
        return {
          presetName: preset.label,
          presetType: "system",
        };
      });
    },
    ...mapState({
      presetList: state => state.filterPreset.list,
      systemList: state => state.filter.quickFilterList,
    }),
  },
  async created() {
    await Promise.all([this.getPresetList(), this.getQuickFilterList()]);
  },
  data() {
    return {
      dialog: false,
      selectedQuickFilter: null,
    };
  },
  methods: {
    addQuickFilter() {
      if (!this.selectedQuickFilter) {
        return;
      }
      this.quickFilterList = [...this.quickFilterList, this.selectedQuickFilter];
      this.$nextTick(() => {
        this.selectedQuickFilter = null;
      });
    },
    getDataCySuffix(value) {
      return value.replace(/ /g, "-");
    },
    loadPreset(preset) {
      this.quickFilterList = [...this.quickFilterList, { presetName: preset.name, presetType: "custom" }];
    },
    removeQuickFilter(index) {
      const list = [...this.quickFilterList];
      list.splice(index, 1);
      this.quickFilterList = list;
    },
    ...mapActions({
      getPresetList: "filterPreset/list",
      getQuickFilterList: "quickFilter/list",
    }),
  },
};
</script>

<style></style>
